import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Verification from './pages/Verification'
import Results from './pages/Results'
import Admin from './pages/Admin'
import Settings from './pages/Settings'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import api from './config/api'
import LeadManagement from './pages/LeadManagement'
import ListVerification from './pages/ListVerification'
import Landing from './pages/Landing'
import Installer from './pages/Installer'
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'
import DownloadLists from './pages/DownloadLists';
import Profile from './pages/Profile';
import Updates from './pages/Updates';
import Satellites from './pages/Satellites';

// Demo Modal Component
const DemoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in demo-modal-container">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-8 border border-slate-200 dark:border-slate-800 animate-scale-in text-center">
        <div className="h-20 w-20 bg-brand-50 dark:bg-brand-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="h-10 w-10 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-6a2 2 0 11-4 0 2 2 0 014 0zm0 0V7a2 2 0 10-4 0v2m4 0V5a2 2 0 10-4 0v2" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Demo Mode Only</h3>
        <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
          This is a demonstration version of Smart Email Verifier. Verification actions and settings changes are disabled in this mode.
        </p>
        <button
          onClick={onClose}
          className="w-full btn btn-primary py-3 rounded-xl font-bold tracking-wide shadow-lg shadow-brand-500/20"
        >
          Got it!
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

function RequireAuth({ children }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  if (isLoading) return null
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />
  return children
}

function AppContent() {
  const [isConfigured, setIsConfigured] = useState(null)
  const [showDemoModal, setShowDemoModal] = useState(false)
  const [appVersion, setAppVersion] = useState('')
  const { isAuthenticated, isLoading } = useAuth()

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/admin/updates/info')
        .then(res => {
          if (res.data.success) {
            setAppVersion(res.data.currentVersion)
          }
        })
        .catch(err => console.error('Failed to fetch version', err))
    }
  }, [isAuthenticated])

  useEffect(() => {
    fetch('/api/setup/status', {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => setIsConfigured(!!data.isConfigured))
      .catch(() => setIsConfigured(false))
  }, [])

  // Global Click Interceptor for Demo Mode
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Allow all buttons on Landing and Login pages so users can actually log in
      if (!isAuthenticated) return;

      // Find the closest button or link with 'btn' class
      const target = e.target.closest('button, a.btn, input[type="submit"], input[type="button"]');

      if (target) {
        const text = target.innerText?.trim().toLowerCase() || '';
        const title = target.getAttribute('title')?.toLowerCase() || '';
        const ariaLabel = target.getAttribute('aria-label')?.toLowerCase() || '';

        // Exclude navigation/UI toggle buttons AND buttons inside the Demo Modal itself
        const isAllowed = target.closest('nav') ||
          target.getAttribute('href')?.startsWith('/') ||
          target.closest('.lg\\:hidden') || // Mobile menu toggle
          text.includes('logout') ||
          text.includes('light') || // Theme toggle
          text.includes('dark') ||  // Theme toggle
          text.includes('guide') || // Settings/Usage guides
          text.includes('add node') || // Open the add node form
          text.includes('cancel') ||   // Cancel buttons
          text.includes('close') ||    // Close buttons
          text.includes('back') ||     // Back buttons
          text === '' ||               // Icon-only buttons (like the X close button)
          title.includes('close') ||   // Title-based close buttons
          ariaLabel.includes('close') ||
          target.getAttribute('type') === 'checkbox' ||
          target.closest('.demo-modal-container'); // Allow clicks inside the modal

        if (!isAllowed) {
          e.preventDefault();
          e.stopPropagation();
          setShowDemoModal(true);
        }
      }
    };

    document.addEventListener('click', handleGlobalClick, true);
    return () => document.removeEventListener('click', handleGlobalClick, true);
  }, [isAuthenticated]);

  if (isLoading || isConfigured === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="flex flex-col items-center gap-4">
            <span className="inline-block h-16 w-16 rounded-full shadow-lg bg-blue-100 dark:bg-gray-800 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" rx="12" fill="#2563eb" />
                <path d="M7 9l5 4 5-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="4" y="7" width="16" height="10" rx="2" stroke="#fff" strokeWidth="2" />
              </svg>
            </span>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading Smart Email Verifier...</p>
        </div>
      </div>
    )
  }

  // Show installer if not configured
  if (!isConfigured) {
    return (
      <Routes>
        <Route path="/setup" element={<Installer />} />
        <Route path="*" element={<Installer />} />
      </Routes>
    )
  }

  // Show landing page if not authenticated (and not in setup mode)
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Layout>
        <Routes>
          <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/verification" element={<RequireAuth><Verification /></RequireAuth>} />
          <Route path="/results" element={<RequireAuth><Results /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
          <Route path="/satellites" element={<RequireAuth><Satellites /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/leads" element={<RequireAuth><LeadManagement /></RequireAuth>} />
          <Route path="/list-verification" element={<RequireAuth><ListVerification /></RequireAuth>} />
          <Route path="/download-lists" element={<RequireAuth><DownloadLists /></RequireAuth>} />
          <Route path="/updates" element={<RequireAuth><Updates /></RequireAuth>} />
          <Route path="*" element={<RequireAuth><Dashboard /></RequireAuth>} />
        </Routes>
      </Layout>
      <DemoModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} />
      {/* Footer */}
      <footer className="w-full mt-auto py-4 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-800 text-center text-xs text-gray-500 dark:text-gray-400">
        <span>&copy; {new Date().getFullYear()} UnCutSolutions. All rights reserved. | Developed by Abdul Wasay Mughal {appVersion && `| v${appVersion}`}</span>
      </footer>
    </div>
  )
}

export default App 